import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { handleContactReveal, sendEmailToTalent } from './utils';

/**
 * Custom hook to manage the ContactPopup state and functionality
 * @param {Object} params - Parameters
 * @param {boolean} params.isOpen - Whether the popup is open
 * @param {Function} params.closePopup - Function to close the popup
 * @param {Object} params.talent - Talent data
 * @returns {Object} - States and functions for the ContactPopup
 */
const useContactPopup = ({ isOpen, closePopup, talent }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const userToken = useSelector(state => state.user.token);
  const features = useSelector(state => state.user.features);
  
  // Component state
  const [option, setOption] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [shouldShowContactPopup, setShouldShowContactPopup] = useState(true);
  
  // Check if talent data is available
  const hasTalentData = talent && Object.keys(talent).length > 0;
  
  // Reset state when the popup is opened/closed
  useEffect(() => {
    if (isOpen) {
      setShouldShowContactPopup(true);
    } else {
      // Reset state when popup is closed
      setOption('');
      setMessage('');
      setSending(false);
      setSuccess(false);
      setError(null);
      setShowContactInfo(false);
      setShowUpgradeModal(false);
    }
  }, [isOpen]);
  
  /**
   * Handle option selection change
   */
  const handleOptionChange = (e) => {
    setOption(e.target.value);
  };
  
  /**
   * Handle message text change
   */
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };
  
  /**
   * Handle the main popup close
   */
  const handlePopupClose = () => {
    closePopup();
  };
  
  /**
   * Handle closing the upgrade modal
   */
  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal(false);
    // Close the whole popup to reset everything
    closePopup();
  };
  
  /**
   * Handle form submission for contact
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (option === 'email' && message.trim() === '') {
      setError(t('ContactPopup.message_required'));
      return;
    }
    
    if (option === 'reveal') {
      await handleContactReveal({
        user,
        talent,
        features,
        userToken,
        t,
        setShowContactInfo,
        setShowUpgradeModal,
        setUpgradeMessage,
        setShouldShowContactPopup
      });
      return;
    }
    
    await sendEmailToTalent({
      user,
      talent,
      message,
      setSending,
      setError,
      setSuccess,
      t,
      features,
      userToken,
      setShowUpgradeModal,
      setUpgradeMessage,
      setShouldShowContactPopup
    });
  };
  
  return {
    // State
    option,
    message,
    sending,
    success,
    error,
    showContactInfo,
    showUpgradeModal,
    upgradeMessage,
    shouldShowContactPopup,
    hasTalentData,
    user,
    userToken,
    features,
    t,
    
    // Functions
    setOption,
    setMessage,
    setSending,
    setSuccess,
    setError,
    setShowContactInfo,
    setShowUpgradeModal,
    setUpgradeMessage,
    setShouldShowContactPopup,
    handleOptionChange,
    handleMessageChange,
    handlePopupClose,
    handleCloseUpgradeModal,
    handleSubmit
  };
};

export default useContactPopup; 